import React, { useEffect, useState } from "react";

const defaultPolicy = {
  key: "",
  scope: "hr",
  value: {
    enabled: false,
    count_sandwiched_days_as_leave: false,
    treat_weekend_or_public_holiday_between_leaves_as_leave: false,
    minimum_consecutive_leave_days: 1,
    apply_to: [],
    notes: "",
  },
  effective_from: "",
  effective_to: "",
  is_active: true,

};

const toDateInput = (value) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch (_) {
    return "";
  }
};

const LeavePolicyForm = ({
  loading,
  closeModal,
  onSubmit,
  initialValues,
  errors = {},
}) => {
  const [formData, setFormData] = useState({
    ...defaultPolicy,
    ...initialValues,
    value: { ...defaultPolicy.value, ...(initialValues?.value || {}) },
    effective_from: toDateInput(initialValues?.effective_from),
    effective_to: toDateInput(initialValues?.effective_to),
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    setFormData((prev) => ({
      ...defaultPolicy,
      ...initialValues,
      value: { ...defaultPolicy.value, ...(initialValues?.value || {}) },
      effective_from: toDateInput(initialValues?.effective_from),
      effective_to: toDateInput(initialValues?.effective_to),
    }));
  }, [initialValues]);

  const setNestedValue = (path, value) => {
    setFormData((prev) => {
      const updated = { ...prev };
      const keys = path.split(".");
      let curr = updated;
      for (let i = 0; i < keys.length - 1; i += 1) {
        const key = keys[i];
        curr[key] = Array.isArray(curr[key]) ? [...curr[key]] : { ...(curr[key] || {}) };
        curr = curr[key];
      }
      curr[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNestedValue(name, value);
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setNestedValue(name, checked);
  };

  const handleApplyToChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => {
      const current = new Set(prev.value.apply_to || []);
      if (checked) current.add(value);
      else current.delete(value);
      return { ...prev, value: { ...prev.value, apply_to: Array.from(current) } };
    });
  };

  const validate = () => {
    const validationErrors = {};
    if (!formData.key || !formData.key.trim()) validationErrors.key = "Key is required.";
    if (!formData.scope || !formData.scope.trim()) validationErrors.scope = "Scope is required.";
    if (!formData.effective_from) validationErrors.effective_from = "Effective from is required.";
    if (!formData.effective_to) validationErrors.effective_to = "Effective to is required.";
    if (!Number.isFinite(Number(formData.value.minimum_consecutive_leave_days))) {
      validationErrors.minimum_consecutive_leave_days = "Minimum consecutive leave days must be a number.";
    }
    return validationErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }
    setFormErrors({});
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 font-poppins">
      <div>
        <label htmlFor="key" className="block dark:text-white text-sm font-medium text-gray-700">
          Key <span className="text-red-500 ml-1">*</span>
        </label>
        <input
          type="text"
          id="key"
          name="key"
          value={formData.key}
          onChange={handleInputChange}
          className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
            formErrors.key ? "border-red-500" : "border-gray-300"
          }`}
          required
        />
        {formErrors.key && <p className="text-red-500 text-sm mt-1">{formErrors.key}</p>}
      </div>

      <div>
        <label htmlFor="scope" className="block dark:text-white text-sm font-medium text-gray-700">
          Scope <span className="text-red-500 ml-1">*</span>
        </label>
        <input
          type="text"
          id="scope"
          name="scope"
          value={formData.scope}
          onChange={handleInputChange}
          className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
            formErrors.scope ? "border-red-500" : "border-gray-300"
          }`}
          required
        />
        {formErrors.scope && <p className="text-red-500 text-sm mt-1">{formErrors.scope}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex items-center gap-2">
          <input
            id="value.enabled"
            name="value.enabled"
            type="checkbox"
            checked={!!formData.value.enabled}
            onChange={handleCheckboxChange}
            className="h-4 w-4"
          />
          <label htmlFor="value.enabled" className="dark:text-white text-sm font-medium text-gray-700">
            Enabled
          </label>
        </div>

        <div className="flex items-center gap-2">
          <input
            id="value.count_sandwiched_days_as_leave"
            name="value.count_sandwiched_days_as_leave"
            type="checkbox"
            checked={!!formData.value.count_sandwiched_days_as_leave}
            onChange={handleCheckboxChange}
            className="h-4 w-4"
          />
          <label htmlFor="value.count_sandwiched_days_as_leave" className="dark:text-white text-sm font-medium text-gray-700">
            Count sandwiched days as leave
          </label>
        </div>

        <div className="flex items-center gap-2 sm:col-span-2">
          <input
            id="value.treat_weekend_or_public_holiday_between_leaves_as_leave"
            name="value.treat_weekend_or_public_holiday_between_leaves_as_leave"
            type="checkbox"
            checked={!!formData.value.treat_weekend_or_public_holiday_between_leaves_as_leave}
            onChange={handleCheckboxChange}
            className="h-4 w-4"
          />
          <label htmlFor="value.treat_weekend_or_public_holiday_between_leaves_as_leave" className="dark:text-white text-sm font-medium text-gray-700">
            Treat weekend/public holiday between leaves as leave
          </label>
        </div>
      </div>

      <div>
        <label htmlFor="value.minimum_consecutive_leave_days" className="block dark:text-white text-sm font-medium text-gray-700">
          Minimum consecutive leave days
        </label>
        <input
          type="number"
          id="value.minimum_consecutive_leave_days"
          name="value.minimum_consecutive_leave_days"
          value={formData.value.minimum_consecutive_leave_days}
          onChange={handleInputChange}
          className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
            formErrors.minimum_consecutive_leave_days ? "border-red-500" : "border-gray-300"
          }`}
          min={0}
        />
        {formErrors.minimum_consecutive_leave_days && (
          <p className="text-red-500 text-sm mt-1">{formErrors.minimum_consecutive_leave_days}</p>
        )}
      </div>

      <div>
        <span className="block dark:text-white text-sm font-medium text-gray-700">Apply to</span>
        <div className="mt-2 flex items-center gap-6">
          <label className="flex items-center gap-2 dark:text-white text-sm text-gray-700">
            <input
              type="checkbox"
              value="full_day"
              checked={formData.value.apply_to?.includes("full_day")}
              onChange={handleApplyToChange}
              className="h-4 w-4"
            />
            Full day
          </label>
          <label className="flex items-center gap-2 dark:text-white text-sm text-gray-700">
            <input
              type="checkbox"
              value="half_day"
              checked={formData.value.apply_to?.includes("half_day")}
              onChange={handleApplyToChange}
              className="h-4 w-4"
            />
            Half day
          </label>
        </div>
      </div>

      <div>
        <label htmlFor="value.notes" className="block dark:text-white text-sm font-medium text-gray-700">
          Notes
        </label>
        <textarea
          id="value.notes"
          name="value.notes"
          value={formData.value.notes}
          onChange={handleInputChange}
          className="mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 border-gray-300"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="effective_from" className="block dark:text-white text-sm font-medium text-gray-700">
            Effective from <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="date"
            id="effective_from"
            name="effective_from"
            value={formData.effective_from}
            onChange={handleInputChange}
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
              formErrors.effective_from ? "border-red-500" : "border-gray-300"
            }`}
            required
          />
          {formErrors.effective_from && <p className="text-red-500 text-sm mt-1">{formErrors.effective_from}</p>}
        </div>

        <div>
          <label htmlFor="effective_to" className="block dark:text-white text-sm font-medium text-gray-700">
            Effective to <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="date"
            id="effective_to"
            name="effective_to"
            value={formData.effective_to}
            onChange={handleInputChange}
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
              formErrors.effective_to ? "border-red-500" : "border-gray-300"
            }`}
            required
          />
          {formErrors.effective_to && <p className="text-red-500 text-sm mt-1">{formErrors.effective_to}</p>}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          id="is_active"
          name="is_active"
          type="checkbox"
          checked={!!formData.is_active}
          onChange={handleCheckboxChange}
          className="h-4 w-4"
        />
        <label htmlFor="is_active" className="dark:text-white text-sm font-medium text-gray-700">
          Active
        </label>
      </div>

      <div className="flex justify-between items-center space-x-4">
        <button
          type="button"
          onClick={closeModal}
          className="px-4 py-2 dark:bg-gray-900 dark:text-white bg-gray-500 text-white rounded-md hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          {initialValues?.id ? "Update" : "Add"}
          {loading && (
            <svg
              className="animate-spin h-5 w-5 ml-2 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a 8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z"
              ></path>
            </svg>
          )}
        </button>
      </div>
    </form>
  );
};

export default LeavePolicyForm;
